import React, { useEffect, useMemo, useRef, useState } from "react";

import {
  Box,
  Button,
  CircularProgress,
  Drawer,
  IconButton,
  Paper,
  Stack,
  Typography,
} from "@mui/material";

import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import CloseIcon from "@mui/icons-material/Close";

import axios from "axios";

import Images from "../utils/Images";
import { API } from "../utils/Api";

import {
  calculateTaxAmount,
  getProductTaxPercent,
  toMoneyNumber,
} from "../utils/tax";

const COLORS = {
  primary: "#08A9EE",
  primaryHover: "#0298DA",
  primaryDisabled: "#A7DDF5",

  primaryText: "#0E5069",
  secondaryText: "#666666",
  mutedText: "#858585",

  pageBackground: "#F4F9FC",
  cardBackground: "#FFFFFF",

  border: "#D4E7EF",
  radioBackground: "#E3F2F8",

  white: "#FFFFFF",

  error: "#D14343",
  success: "#009688",
};

const ProductDrawer = ({
  open,
  product: listingProduct,
  outletData,
  onClose,
  onAddToCart,
  onMessage,
}) => {
  const addonGroupRefs = useRef({});

  const [selectedAddons, setSelectedAddons] = useState({});
  const [missingRequiredAddonId, setMissingRequiredAddonId] = useState(null);

  const [exhaustedAddonId, setExhaustedAddonId] = useState(null);

  const [quantity, setQuantity] = useState(1);

  const [addingToCart, setAddingToCart] = useState(false);

  const [detailedProduct, setDetailedProduct] = useState(null);

  const [detailsLoading, setDetailsLoading] = useState(false);

  const product = detailedProduct || listingProduct;

  /*
    |--------------------------------------------------------------------------
    | BASIC HELPERS
    |--------------------------------------------------------------------------
    */

  const getId = (item) => {
    if (!item) {
      return null;
    }

    if (typeof item === "string") {
      return item;
    }

    return item?._id || item?.value || null;
  };

  const getSelectedValueId = (addonValue) => {
    if (addonValue?.value && typeof addonValue.value === "object") {
      return getId(addonValue.value) || addonValue?._id || null;
    }

    return addonValue?._id || null;
  };

  const getSelectedValueName = (addonValue) => {
    if (typeof addonValue?.value === "object") {
      return (
        addonValue?.value?.value ||
        addonValue?.value?.label ||
        addonValue?.value?.name ||
        addonValue?.label ||
        addonValue?.subLabel ||
        "Addon"
      );
    }

    return (
      addonValue?.label || addonValue?.value || addonValue?.subLabel || "Addon"
    );
  };

  const getProductPrice = (currentProduct) =>
    Number(
      currentProduct?.newOfferPrice ||
        currentProduct?.offerPrice ||
        currentProduct?.price ||
        0,
    );

  const getProductMrpRate = (currentProduct) =>
    Number(
      currentProduct?.newMrp ??
        currentProduct?.mrpRate ??
        currentProduct?.mrp ??
        currentProduct?.defaultMrp ??
        currentProduct?.price ??
        getProductPrice(currentProduct),
    );

  const getProductImage = (currentProduct) =>
    currentProduct?.displayImageUrl ||
    currentProduct?.displayImage ||
    currentProduct?.imageUrl ||
    currentProduct?.image ||
    Images.product;

  const getPreparationTime = (currentProduct) =>
    currentProduct?.makingTime ||
    currentProduct?.preparationTime ||
    currentProduct?.preparation_time ||
    currentProduct?.prepTime ||
    null;

  const getCalories = (currentProduct) =>
    currentProduct?.calories ||
    currentProduct?.kcal ||
    currentProduct?.nutrition?.calories ||
    null;

  const getDescription = (currentProduct) =>
    currentProduct?.description ||
    currentProduct?.shortDescription ||
    currentProduct?.subTitle ||
    "";

  const getFoodType = (currentProduct) => {
    const rawValue =
      currentProduct?.foodType ||
      currentProduct?.dietType ||
      currentProduct?.type ||
      "";

    return String(rawValue).toUpperCase();
  };

  const getAddonValueLabel = (addonValue) => {
    if (typeof addonValue?.value === "object") {
      return (
        addonValue?.value?.value ||
        addonValue?.value?.label ||
        addonValue?.value?.name ||
        addonValue?.subLabel ||
        "Addon"
      );
    }

    return addonValue?.value || addonValue?.subLabel || "Addon";
  };

  /*
    |--------------------------------------------------------------------------
    | OUTLET / ADDON AVAILABILITY
    |--------------------------------------------------------------------------
    */

  const isOutletExcluded = (excludedOutlets = []) => {
    if (!Array.isArray(excludedOutlets) || !outletData?._id) {
      return false;
    }

    return excludedOutlets.some((outlet) => getId(outlet) === outletData._id);
  };

  const normalizeExcludedValues = (excludedValues = []) => {
    if (!Array.isArray(excludedValues)) {
      return [];
    }

    return excludedValues.map((value) => getId(value)).filter(Boolean);
  };

  const isDefaultAddonValue = (addonValue) => {
    const populatedValue =
      typeof addonValue?.value === "object" ? addonValue.value : {};

    return (
      addonValue?.isDefault === true ||
      addonValue?.default === true ||
      addonValue?.isDefaultValue === true ||
      addonValue?.selectedByDefault === true ||
      addonValue?.isSelected === true ||
      populatedValue?.isDefault === true ||
      populatedValue?.default === true ||
      populatedValue?.isDefaultValue === true ||
      populatedValue?.selectedByDefault === true ||
      populatedValue?.isSelected === true
    );
  };

  const isAddonValueExhausted = (addonValue) => {
    const populatedValue =
      typeof addonValue?.value === "object" ? addonValue.value : {};

    return Boolean(
      addonValue?.isExhausted ||
      addonValue?.isOutOfStock ||
      addonValue?.outOfStock ||
      addonValue?.isAvailable === false ||
      populatedValue?.isExhausted ||
      populatedValue?.isOutOfStock ||
      populatedValue?.outOfStock ||
      populatedValue?.isAvailable === false,
    );
  };

  const getAddonValueStatus = (addonValue) => {
    const populatedValue =
      typeof addonValue?.value === "object" ? addonValue.value : {};

    return addonValue?.status || populatedValue?.status || "ACTIVE";
  };

  const isAddonValueInactive = (addonValue) =>
    getAddonValueStatus(addonValue) === "INACTIVE";

  const hasAddonValueIconInfo = (addonValue) => {
    const populatedValue =
      typeof addonValue?.value === "object" ? addonValue.value : {};

    return Boolean(addonValue?.iconInfo || populatedValue?.iconInfo);
  };

  const isAddonValueOutletExcluded = (addonValue) => {
    const populatedValue =
      typeof addonValue?.value === "object" ? addonValue.value : {};

    return (
      isOutletExcluded(addonValue?.excludedOutlets || []) ||
      isOutletExcluded(populatedValue?.excludedOutlets || [])
    );
  };

  const isAddonValueUnavailableDefault = (addonValue) =>
    Boolean(
      addonValue?.isDefault &&
      (addonValue?.isInactive ||
        addonValue?.isOutletExcluded ||
        addonValue?.isInExcludedValues),
    );

  const getAddonValueOrderType = (addonValue) => {
    const populatedValue =
      typeof addonValue?.value === "object" ? addonValue.value : {};

    return String(addonValue?.orderType || populatedValue?.orderType || "ALL")
      .trim()
      .toUpperCase();
  };

  const isAddonValueAllowedForPickup = (addonValue) => {
    const orderType = getAddonValueOrderType(addonValue);

    return orderType === "PICKUP" || orderType === "ALL";
  };

  /*
    |--------------------------------------------------------------------------
    | BUILD VISIBLE ADDONS
    |--------------------------------------------------------------------------
    */

  const buildVisibleAddons = (currentProduct) => {
    const productAddons = Array.isArray(currentProduct?.addons)
      ? currentProduct.addons
      : [];

    return productAddons
      .filter((allocation) => {
        if (allocation?.status === "INACTIVE") {
          return false;
        }

        return !isOutletExcluded(allocation?.excludedOutlets || []);
      })
      .map((allocation) => {
        const addonMaster =
          allocation?.addon && typeof allocation.addon === "object"
            ? allocation.addon
            : allocation;

        const excludedValueIds = normalizeExcludedValues(
          allocation?.excludedValues || [],
        );

        const allocationValues = Array.isArray(allocation?.values)
          ? allocation.values
          : [];

        const values = allocationValues
          .filter((valueItem) => {
            if (hasAddonValueIconInfo(valueItem)) {
              return false;
            }

            if (!isAddonValueAllowedForPickup(valueItem)) {
              return false;
            }

            const isDefault = isDefaultAddonValue(valueItem);

            const isInactive = isAddonValueInactive(valueItem);

            const isValueOutletExcluded = isAddonValueOutletExcluded(valueItem);

            const valueId = getId(valueItem?.value);

            const isInExcludedValues = Boolean(
              valueId && excludedValueIds.includes(valueId),
            );

            return (
              (!isInactive || isDefault) &&
              (!isValueOutletExcluded || isDefault) &&
              (!isInExcludedValues || isDefault)
            );
          })
          .map((valueItem) => {
            const isDefault = isDefaultAddonValue(valueItem);

            const isInactive = isAddonValueInactive(valueItem);

            const isValueOutletExcluded = isAddonValueOutletExcluded(valueItem);

            const valueId = getId(valueItem?.value);

            const isInExcludedValues = Boolean(
              valueId && excludedValueIds.includes(valueId),
            );

            return {
              ...valueItem,

              label: getAddonValueLabel(valueItem),

              price: Number(valueItem?.price || 0),

              status: getAddonValueStatus(valueItem),

              isDefault,

              isExhausted: isAddonValueExhausted(valueItem),

              isInactive,

              isOutletExcluded: isValueOutletExcluded,

              isInExcludedValues,

              isDisabled:
                isValueOutletExcluded ||
                isInExcludedValues ||
                (isDefault && isInactive),
            };
          });

        return {
          ...allocation,

          title: addonMaster?.title || allocation?.title || "Choose addon",

          isOptional: addonMaster?.isOptional ?? allocation?.isOptional ?? true,

          isMultipleSelect:
            addonMaster?.isMultipleSelect ??
            allocation?.isMultipleSelect ??
            false,

          values,
        };
      })
      .filter((addon) =>
        addon.values.some(
          (addonValue) =>
            !addonValue?.isInactive &&
            !addonValue?.isOutletExcluded &&
            !addonValue?.isInExcludedValues,
        ),
      );
  };

  /*
    |--------------------------------------------------------------------------
    | VISIBLE ADDONS
    |--------------------------------------------------------------------------
    */

  const visibleAddons = useMemo(() => {
    const addons = buildVisibleAddons(product);

    return [...addons].sort((firstAddon, secondAddon) => {
      const firstIsSize =
        String(firstAddon?.title || "")
          .trim()
          .toLowerCase() === "size";

      const secondIsSize =
        String(secondAddon?.title || "")
          .trim()
          .toLowerCase() === "size";

      if (firstIsSize === secondIsSize) {
        return 0;
      }

      return firstIsSize ? -1 : 1;
    });
  }, [product, outletData?._id]);

  const normalizeOutletIds = (outlets = []) =>
    (Array.isArray(outlets) ? outlets : [])
      .map((outlet) => getId(outlet))
      .filter(Boolean);

  /*
    |--------------------------------------------------------------------------
    | PREPARE SELECTED ADDON
    |--------------------------------------------------------------------------
    */

  const prepareSelectedAddonValue = (addon, addonValue) => ({
    ...addonValue,

    addonAllocationId: addon?._id,

    addonId: getId(addon?.addon) || addon?._id,

    addonTitle: addon?.title,

    addonExcludedOutlets: normalizeOutletIds(addon?.excludedOutlets || []),

    excludedOutlets: normalizeOutletIds(addonValue?.excludedOutlets || []),

    status: getAddonValueStatus(addonValue),

    isInactive: Boolean(addonValue?.isInactive),

    isOutletExcluded: Boolean(addonValue?.isOutletExcluded),

    isInExcludedValues: Boolean(addonValue?.isInExcludedValues),

    isDisabled: Boolean(addonValue?.isDisabled),

    isExhausted: isAddonValueExhausted(addonValue),
  });

  /*
    |--------------------------------------------------------------------------
    | DEFAULT ADDON SELECTIONS
    |--------------------------------------------------------------------------
    */

  const buildDefaultAddonSelections = (addons = []) => {
    return addons.reduce((selections, addon) => {
      const defaultValues = (addon?.values || []).filter(
        (addonValue) =>
          isDefaultAddonValue(addonValue) &&
          !addonValue?.isExhausted &&
          !addonValue?.isInactive &&
          !addonValue?.isOutletExcluded &&
          !addonValue?.isInExcludedValues,
      );

      if (defaultValues.length === 0) {
        return selections;
      }

      const valuesToSelect = addon?.isMultipleSelect
        ? defaultValues
        : defaultValues.slice(0, 1);

      selections[addon?._id] = valuesToSelect.map((addonValue) =>
        prepareSelectedAddonValue(addon, addonValue),
      );

      return selections;
    }, {});
  };

  /*
    |--------------------------------------------------------------------------
    | PRICE CALCULATIONS
    |--------------------------------------------------------------------------
    */

  const selectedAddonValues = useMemo(
    () =>
      Object.values(selectedAddons).flatMap((values) =>
        Array.isArray(values) ? values : [],
      ),
    [selectedAddons],
  );

  const addonsTotal = useMemo(
    () =>
      toMoneyNumber(
        selectedAddonValues.reduce(
          (total, addonValue) => total + Number(addonValue?.price || 0),
          0,
        ),
      ),
    [selectedAddonValues],
  );

  const currentItemUnitPrice = useMemo(
    () => toMoneyNumber(getProductPrice(product) + addonsTotal),
    [product, addonsTotal],
  );

  const productTaxPercent = useMemo(
    () => getProductTaxPercent(product),
    [product],
  );

  const currentItemTax = useMemo(
    () => calculateTaxAmount(currentItemUnitPrice, quantity, productTaxPercent),
    [currentItemUnitPrice, quantity, productTaxPercent],
  );

  const currentItemTotal = currentItemTax.taxable;

  /*
    |--------------------------------------------------------------------------
    | RESET / CLOSE
    |--------------------------------------------------------------------------
    */

  const resetDrawer = () => {
    setSelectedAddons({});

    setMissingRequiredAddonId(null);

    setExhaustedAddonId(null);

    setQuantity(1);

    setAddingToCart(false);

    addonGroupRefs.current = {};
  };

  const handleClose = () => {
    resetDrawer();

    setDetailedProduct(null);

    setDetailsLoading(false);

    onClose?.();
  };

  const showMessage = (text, severity = "success") => {
    onMessage?.(text, severity);
  };

  /*
    |--------------------------------------------------------------------------
    | CHECK SELECTED VALUE
    |--------------------------------------------------------------------------
    */

  const isAddonValueSelected = (addonAllocationId, addonValueId) => {
    const values = selectedAddons?.[addonAllocationId] || [];

    return values.some((value) => value?._id === addonValueId);
  };

  /*
    |--------------------------------------------------------------------------
    | ADDON SELECTION
    |--------------------------------------------------------------------------
    */

  const handleAddonSelection = (addon, addonValue) => {
    const existingValues = selectedAddons?.[addon?._id] || [];

    const alreadySelected = existingValues.some(
      (value) => value?._id === addonValue?._id,
    );

    if (addonValue?.isDisabled && !alreadySelected) {
      if (isAddonValueUnavailableDefault(addonValue)) {
        if (addonValue?.isInactive) {
          showMessage(
            "This default addon is currently unavailable.",
            "warning",
          );
        } else {
          showMessage("This addon is not available at this outlet.", "warning");
        }
      }

      return;
    }

    if (addonValue?.isExhausted && !alreadySelected) {
      return;
    }

    setMissingRequiredAddonId((currentMissingId) =>
      currentMissingId === addon?._id ? null : currentMissingId,
    );

    setExhaustedAddonId((currentExhaustedId) =>
      currentExhaustedId === addon?._id ? null : currentExhaustedId,
    );

    setSelectedAddons((currentSelections) => {
      const currentValues = currentSelections?.[addon?._id] || [];

      const isAlreadySelected = currentValues.some(
        (value) => value?._id === addonValue?._id,
      );

      if (addon?.isMultipleSelect) {
        return {
          ...currentSelections,

          [addon?._id]: isAlreadySelected
            ? currentValues.filter((value) => value?._id !== addonValue?._id)
            : [...currentValues, prepareSelectedAddonValue(addon, addonValue)],
        };
      }

      if (isAlreadySelected && addon?.isOptional) {
        return {
          ...currentSelections,

          [addon?._id]: [],
        };
      }

      return {
        ...currentSelections,

        [addon?._id]: [prepareSelectedAddonValue(addon, addonValue)],
      };
    });
  };

  /*
    |--------------------------------------------------------------------------
    | SCROLL TO INVALID ADDON
    |--------------------------------------------------------------------------
    */

  const scrollToAddonGroup = (addonId) => {
    window.requestAnimationFrame(() => {
      addonGroupRefs.current[addonId]?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    });
  };

  /*
    |--------------------------------------------------------------------------
    | VALIDATION
    |--------------------------------------------------------------------------
    */

  const validateRequiredAddons = () => {
    const exhaustedAddon = visibleAddons.find((addon) => {
      const selectedValues = selectedAddons?.[addon?._id] || [];

      return selectedValues.some((value) => value?.isExhausted);
    });

    if (exhaustedAddon) {
      setMissingRequiredAddonId(null);

      setExhaustedAddonId(exhaustedAddon?._id);

      scrollToAddonGroup(exhaustedAddon?._id);

      showMessage(
        `Please select another value for ${
          exhaustedAddon?.title || "this addon"
        }.`,
        "warning",
      );

      return false;
    }

    const unavailableDefaultAddons = visibleAddons.filter((addon) => {
      const hasDefaultExcluded = (addon?.values || []).some(
        (addonValue) =>
          addonValue?.isDefault &&
          (addonValue?.isOutletExcluded || addonValue?.isInExcludedValues),
      );

      const hasDefaultInactive = (addon?.values || []).some(
        (addonValue) => addonValue?.isDefault && addonValue?.isInactive,
      );

      if (!hasDefaultExcluded && !hasDefaultInactive) {
        return false;
      }

      const selected = selectedAddons?.[addon?._id] || [];

      if (addon?.isMultipleSelect) {
        return selected.length === 0;
      }

      const selectedValue = selected[0];

      return (
        !selectedValue ||
        selectedValue?.isOutletExcluded ||
        selectedValue?.isInExcludedValues ||
        selectedValue?.isInactive ||
        selectedValue?.status === "INACTIVE"
      );
    });

    if (unavailableDefaultAddons.length > 0) {
      const firstInvalidAddon = unavailableDefaultAddons[0];

      setExhaustedAddonId(null);

      setMissingRequiredAddonId(firstInvalidAddon?._id);

      scrollToAddonGroup(firstInvalidAddon?._id);

      showMessage(
        `Please select other available options for: ${unavailableDefaultAddons
          .map((addon) => addon?.title)
          .filter(Boolean)
          .join(", ")}`,
        "warning",
      );

      return false;
    }

    const missingAddon = visibleAddons.find((addon) => {
      if (addon?.isOptional) {
        return false;
      }

      return (selectedAddons?.[addon?._id] || []).length === 0;
    });

    if (!missingAddon) {
      setMissingRequiredAddonId(null);

      setExhaustedAddonId(null);

      return true;
    }

    setExhaustedAddonId(null);

    setMissingRequiredAddonId(missingAddon?._id);

    scrollToAddonGroup(missingAddon?._id);

    showMessage(`Please select ${missingAddon?.title}.`, "warning");

    return false;
  };

  /*
    |--------------------------------------------------------------------------
    | ADD TO CART
    |--------------------------------------------------------------------------
    */

  const handleAddToCart = async () => {
    if (!product?._id) {
      showMessage("Please select a product.", "warning");

      return;
    }

    if (detailsLoading) {
      return;
    }

    if (listingProduct?.hasAddon && !detailedProduct) {
      showMessage("Unable to load product options. Please try again.", "error");

      return;
    }

    if (!validateRequiredAddons()) {
      return;
    }

    try {
      setAddingToCart(true);

      const addons = selectedAddonValues.map((addonValue) => {
        const label = addonValue?.label || getSelectedValueName(addonValue);

        const selectedValueId = getSelectedValueId(addonValue);

        return {
          _id: addonValue?._id,

          valueId: selectedValueId,

          selectedValue: label,

          selectedValueId,

          addonId: addonValue?.addonId,

          addonAllocationId:
            addonValue?.addonAllocationId || addonValue?.addonId || null,

          addonTitle: addonValue?.addonTitle,

          label,

          price: Number(addonValue?.price || 0),

          subLabel: addonValue?.subLabel || null,

          excludedOutlets: addonValue?.excludedOutlets || [],

          addonExcludedOutlets: addonValue?.addonExcludedOutlets || [],
        };
      });

      const productPrice = getProductPrice(product);

      const mrpRate = getProductMrpRate(product);

      const addonPrice = toMoneyNumber(
        addons.reduce((total, addon) => total + Number(addon?.price || 0), 0),
      );

      const unitPrice = toMoneyNumber(productPrice + addonPrice);

      const itemProductTaxPercent = getProductTaxPercent(product);

      await onAddToCart?.({
        productId: product._id,

        productName: product?.name,

        productImage: getProductImage(product),

        categoryId: product?.category?._id || null,

        categoryName: product?.category?.name || null,

        description: getDescription(product),

        productPrice,

        mrpRate,

        addons,

        addonPrice,

        unitPrice,

        quantity,

        productTaxPercent: itemProductTaxPercent,

        makingTime: Number(getPreparationTime(product) || 20),

        menuGroup:
          product?.menuGroup?._id ||
          product?.menuGroup ||
          product?.menuGroupId ||
          null,

        hasAddon:
          Boolean(listingProduct?.hasAddon || product?.hasAddon) ||
          visibleAddons.length > 0 ||
          addons.length > 0,

        outletId: outletData?._id || null,
      });

      handleClose();
    } catch (error) {
      console.error("Unable to add product:", error);

      showMessage("Unable to add product to cart.", "error");
    } finally {
      setAddingToCart(false);
    }
  };

  /*
    |--------------------------------------------------------------------------
    | FETCH PRODUCT DETAILS
    |--------------------------------------------------------------------------
    */

  useEffect(() => {
    if (!open || !listingProduct?._id || !outletData?._id) {
      return undefined;
    }

    let cancelled = false;

    const abortController = new AbortController();

    const fetchProductDetails = async () => {
      setDetailedProduct(null);

      setDetailsLoading(true);

      try {
        const response = await axios.get(
          `${API.BASE_URL}product/get-product-category-addon-details/${listingProduct._id}/${outletData._id}/PICKUP`,
          {
            signal: abortController.signal,
          },
        );

        const responseData = response?.data?.data || response?.data || {};

        const fetchedProduct =
          responseData?.product ||
          (responseData?._id ? responseData : null);

        if (cancelled || !fetchedProduct?._id) {
          return;
        }

        const fetchedCategory = fetchedProduct?.category;

        setDetailedProduct({
          ...listingProduct,
          ...fetchedProduct,
          category:
            fetchedCategory && typeof fetchedCategory === "object"
              ? fetchedCategory
              : listingProduct?.category,
        });
      } catch (error) {
        if (
          cancelled ||
          error?.code === "ERR_CANCELED" ||
          error?.name === "CanceledError" ||
          axios.isCancel?.(error)
        ) {
          return;
        }

        console.error(
          "Error fetching product details:",
          error?.response?.data || error,
        );

        onMessage?.(
          error?.response?.data?.message ||
            "Unable to fetch product details.",
          "error",
        );
      } finally {
        if (!cancelled) {
          setDetailsLoading(false);
        }
      }
    };

    fetchProductDetails();

    return () => {
      cancelled = true;

      abortController.abort();
    };
  }, [open, listingProduct?._id, outletData?._id]);

  /*
    |--------------------------------------------------------------------------
    | INITIALISE DRAWER
    |--------------------------------------------------------------------------
    */

  useEffect(() => {
    if (!open || !product?._id || detailsLoading) {
      return;
    }

    setMissingRequiredAddonId(null);

    setExhaustedAddonId(null);

    setQuantity(1);

    setAddingToCart(false);

    addonGroupRefs.current = {};

    setSelectedAddons(buildDefaultAddonSelections(visibleAddons));
  }, [open, product?._id, visibleAddons, detailsLoading]);

  /*
    |--------------------------------------------------------------------------
    | DISPLAY VALUES
    |--------------------------------------------------------------------------
    */

  const calories = getCalories(product);

  const preparationTime = getPreparationTime(product);

  const description = getDescription(product);

  const foodType = getFoodType(product);

  const weight = product?.weight;

  const weightUnit = product?.weightUnit;

  const productMrp = getProductMrpRate(product);

  const productPrice = getProductPrice(product);

  const discountPercent =
    productMrp > productPrice
      ? Math.round(((productMrp - productPrice) / productMrp) * 100)
      : Number(
          product?.discountPercent ||
            product?.discount ||
            product?.offerDiscount ||
            0,
        );

  /*
    |--------------------------------------------------------------------------
    | UI
    |--------------------------------------------------------------------------
    */

  return (
    <Drawer
      anchor="bottom"
      open={open}
      onClose={handleClose}
      PaperProps={{
        sx: {
          width: "100%",

          maxWidth: 480,

          mx: "auto",

          height: "100dvh",

          maxHeight: "100dvh",

          overflow: "hidden",

          borderRadius: 0,

          backgroundColor: COLORS.pageBackground,
        },
      }}
    >
      {product && (
        <Box
          sx={{
            width: "100%",

            height: "100%",

            minHeight: 0,

            display: "flex",

            flexDirection: "column",

            backgroundColor: COLORS.pageBackground,
          }}
        >
          {/* ================================================
                        SCROLLABLE CONTENT
                    ================================================= */}
          <Box
            className="custom-scrollbar"
            sx={{
              flex: 1,

              minHeight: 0,

              overflowY: "auto",

              overflowX: "hidden",

              overscrollBehavior: "contain",

              WebkitOverflowScrolling: "touch",
            }}
          >
            {/* ================================================
                            PRODUCT IMAGE
                        ================================================= */}
            <Box
              sx={{
                width: "100%",

                height: {
                  xs: 370,

                  sm: 400,
                },

                position: "relative",

                overflow: "hidden",

                backgroundColor: "#DFF4FC",
              }}
            >
              <IconButton
                onClick={handleClose}
                aria-label="Close product drawer"
                sx={{
                  position: "absolute",

                  top: "max(18px, env(safe-area-inset-top))",

                  left: 17,

                  zIndex: 5,

                  width: 38,

                  height: 38,

                  padding: 0,

                  color: "#61727A",

                  backgroundColor: "rgba(255,255,255,0.78)",

                  border: "1px solid rgba(255,255,255,0.95)",

                  boxShadow: "0 2px 9px rgba(0,0,0,0.12)",

                  backdropFilter: "blur(8px)",

                  WebkitBackdropFilter: "blur(8px)",

                  "&:hover": {
                    backgroundColor: "#FFFFFF",
                  },
                }}
              >
                <CloseIcon
                  sx={{
                    fontSize: 24,
                  }}
                />
              </IconButton>

              <Box
                component="img"
                src={product?.coverImageUrl}
                alt={product?.name || "Product"}
                sx={{
                  width: "100%",

                  height: "100%",

                  display: "block",

                  objectFit: "contain",

                  objectPosition: "center",

                  backgroundColor: "#DFF4FC",
                }}
              />
            </Box>

            {/* ================================================
                            PRODUCT DETAILS
                        ================================================= */}
            <Box
              sx={{
                px: 2,

                pt: 1.5,

                pb: 3,

                backgroundColor: COLORS.pageBackground,
              }}
            >
              {/* TOP META ROW */}
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                spacing={1}
              >
                <Typography
                  sx={{
                    minWidth: 0,

                    overflow: "hidden",

                    whiteSpace: "nowrap",

                    textOverflow: "ellipsis",

                    fontSize: 11,

                    fontWeight: 400,

                    color: "#888888",

                    textTransform: "uppercase",
                  }}
                >
                  {product?.category?.name || product?.brand || ""}
                </Typography>

                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={0.65}
                  sx={{
                    flexShrink: 0,
                  }}
                >
                  {calories && (
                    <Typography
                      sx={{
                        fontSize: 11,
                        fontWeight: 600,
                        color: "#626262",
                      }}
                    >
                      {calories} kal
                    </Typography>
                  )}

                  {(calories || preparationTime) && weight && <MetaSeparator />}

                  {weight && (
                    <Typography
                      sx={{
                        fontSize: 11,
                        fontWeight: 600,
                        color: "#626262",
                      }}
                    >
                      {weight} {weightUnit || ""}
                    </Typography>
                  )}

                  {foodType && <FoodTypeIndicator type={foodType} />}
                </Stack>
              </Stack>

              {/* PRODUCT NAME */}
              <Typography
                sx={{
                  mt: 1.35,

                  fontSize: {
                    xs: 21,

                    sm: 23,
                  },

                  lineHeight: 1.18,

                  fontWeight: 700,

                  letterSpacing: "-0.25px",

                  color: COLORS.primaryText,
                }}
              >
                {product?.name}
              </Typography>

              {/* DESCRIPTION */}
              {description && (
                <Typography
                  sx={{
                    mt: 0.55,

                    maxWidth: "96%",

                    fontSize: 13.5,

                    lineHeight: 1.25,

                    fontWeight: 400,

                    color: "#878787",
                  }}
                >
                  {description}
                </Typography>
              )}

              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                spacing={2}
                sx={{
                  mt: 2.1,
                }}
              >
                <Box>
                  <Typography
                    sx={{
                      fontSize: 28,

                      lineHeight: 1,

                      fontWeight: 700,

                      letterSpacing: "-0.5px",

                      color: COLORS.primary,
                    }}
                  >
                    ₹{productPrice}
                  </Typography>
                </Box>

                {/* {discountPercent > 0 && (
                  <ProductOffer mrp={productMrp} discount={discountPercent} />
                )} */}
              </Stack>

              {detailsLoading && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    py: 4,
                  }}
                >
                  <CircularProgress
                    size={28}
                    sx={{
                      color: COLORS.primary,
                    }}
                  />
                </Box>
              )}

              {!detailsLoading && visibleAddons.length > 0 && (
                <Stack
                  spacing={1.5}
                  sx={{
                    mt: 3,
                  }}
                >
                  {visibleAddons.map((addon, index) => (
                    <Box
                      key={addon?._id}
                      ref={(element) => {
                        addonGroupRefs.current[addon?._id] = element;
                      }}
                    >
                      <AddonGroup
                        addon={addon}
                        index={index}
                        selectedAddons={selectedAddons}
                        isSelected={isAddonValueSelected}
                        onSelect={handleAddonSelection}
                        showValidationError={
                          missingRequiredAddonId === addon?._id
                        }
                        showExhaustedError={exhaustedAddonId === addon?._id}
                      />
                    </Box>
                  ))}
                </Stack>
              )}
            </Box>
          </Box>

          {/* ================================================
                        FIXED BOTTOM CART SECTION
                    ================================================= */}
          <Paper
            elevation={0}
            sx={{
              flexShrink: 0,

              position: "relative",

              zIndex: 10,

              px: 2,

              pt: 1.1,

              pb: "max(14px, env(safe-area-inset-bottom))",

              backgroundColor: "rgba(255,255,255,0.98)",

              borderTop: `1px solid ${COLORS.border}`,

              boxShadow: "0 -5px 20px rgba(25,74,94,0.06)",

              backdropFilter: "blur(12px)",

              WebkitBackdropFilter: "blur(12px)",
            }}
          >
            <Stack direction="row" spacing={1.1}>
              <QuantitySelector
                quantity={quantity}
                onDecrease={() =>
                  setQuantity((current) => Math.max(1, current - 1))
                }
                onIncrease={() => setQuantity((current) => current + 1)}
              />

              <Button
                fullWidth
                variant="contained"
                disabled={
                  addingToCart ||
                  detailsLoading ||
                  (Boolean(listingProduct?.hasAddon) && !detailedProduct)
                }
                onClick={handleAddToCart}
                sx={{
                  minHeight: 54,

                  borderRadius: "27px",

                  backgroundColor: COLORS.primary,

                  color: "#FFFFFF",

                  boxShadow: "none",

                  fontSize: 14,

                  fontWeight: 700,

                  textTransform: "none",

                  "&:hover": {
                    backgroundColor: COLORS.primaryHover,

                    boxShadow: "none",
                  },

                  "&.Mui-disabled": {
                    backgroundColor: COLORS.primaryDisabled,

                    color: "#FFFFFF",
                  },
                }}
              >
                {addingToCart ? (
                  <CircularProgress
                    size={20}
                    sx={{
                      color: "#FFFFFF",
                    }}
                  />
                ) : (
                  `Add to Cart · ₹${currentItemTotal.toFixed(2)}`
                )}
              </Button>
            </Stack>
          </Paper>
        </Box>
      )}
    </Drawer>
  );
};

/*
|--------------------------------------------------------------------------
| META SEPARATOR
|--------------------------------------------------------------------------
*/

const MetaSeparator = () => (
  <Box
    sx={{
      width: "1px",

      height: 14,

      backgroundColor: "#969696",

      flexShrink: 0,
    }}
  />
);

/*
|--------------------------------------------------------------------------
| FOOD TYPE
|--------------------------------------------------------------------------
*/

const FoodTypeIndicator = ({ type }) => {
  const normalizedType = String(type).toUpperCase();

  const isNonVeg =
    normalizedType === "NON_VEG" ||
    normalizedType === "NONVEG" ||
    normalizedType === "NON-VEG";

  const color = isNonVeg ? "#C62828" : "#00978B";

  return (
    <Box
      sx={{
        width: 18,

        height: 18,

        flexShrink: 0,

        borderRadius: "4px",

        border: `1.7px solid ${color}`,

        backgroundColor: COLORS.white,

        display: "grid",

        placeItems: "center",
      }}
    >
      <Box
        sx={{
          width: 9,

          height: 9,

          borderRadius: "50%",

          backgroundColor: color,
        }}
      />
    </Box>
  );
};

/*
|--------------------------------------------------------------------------
| PRODUCT OFFER
|--------------------------------------------------------------------------
*/

const OFFER_SKEW = "-15deg";
const OFFER_UNSKEW = "15deg";

const ProductOffer = ({ mrp, discount }) => {
  return (
    <Box
      sx={{
        display: "flex",

        flexDirection: "column",

        alignItems: "center",

        px: 0.75,
      }}
    >
      <Box
        sx={{
          display: "inline-flex",

          alignItems: "stretch",

          height: 28,

          transform: `skewX(${OFFER_SKEW})`,

          overflow: "hidden",

          borderRadius: "3px",
        }}
      >
        {mrp > 0 && (
          <Box
            sx={{
              px: 1.35,

              display: "flex",

              alignItems: "center",

              justifyContent: "center",

              backgroundColor: "#FFFFFF",
            }}
          >
            <Typography
              component="span"
              sx={{
                transform: `skewX(${OFFER_UNSKEW})`,

                display: "inline-flex",

                alignItems: "baseline",

                whiteSpace: "nowrap",

                lineHeight: 1,

                fontWeight: 800,

                color: COLORS.primary,
              }}
            >
              <Box
                component="span"
                sx={{
                  fontSize: 11,

                  fontWeight: 800,

                  mr: "1px",
                }}
              >
                ₹
              </Box>
              <Box
                component="span"
                sx={{
                  fontSize: 14,

                  fontWeight: 800,

                  letterSpacing: "-0.3px",
                }}
              >
                {mrp}
              </Box>
            </Typography>
          </Box>
        )}

        <Box
          sx={{
            px: 1.45,

            display: "flex",

            alignItems: "center",

            justifyContent: "center",

            backgroundColor: COLORS.primary,
          }}
        >
          <Typography
            component="span"
            sx={{
              transform: `skewX(${OFFER_UNSKEW})`,

              whiteSpace: "nowrap",

              fontSize: 11.5,

              fontWeight: 800,

              letterSpacing: "0.2px",

              lineHeight: 1,

              color: "#FFFFFF",

              textTransform: "uppercase",
            }}
          >
            {discount}% OFF
          </Typography>
        </Box>
      </Box>

      <Typography
        sx={{
          mt: 0.55,

          fontSize: 10,

          fontWeight: 700,

          lineHeight: 1,

          letterSpacing: "-0.15px",

          color: COLORS.primary,

          whiteSpace: "nowrap",
        }}
      >
        App Exclusive Offer Price!
      </Typography>
    </Box>
  );
};

/*
|--------------------------------------------------------------------------
| ADDON GROUP
|--------------------------------------------------------------------------
*/

const AddonGroup = ({
  addon,
  index,
  selectedAddons,
  isSelected,
  onSelect,
  showValidationError,
  showExhaustedError,
}) => {
  const selectedValues = selectedAddons?.[addon?._id] || [];

  const selectedCount = selectedValues.length;

  const hasExplicitSelection = selectedCount > 0;

  const hasUnavailableDefault = (addon?.values || []).some((addonValue) =>
    Boolean(addonValue?.isDefault && addonValue?.isDisabled),
  );

  const needsReplacementSelection =
    hasUnavailableDefault && !hasExplicitSelection;

  const hasExhaustedSelection = selectedValues.some((value) => {
    const liveValue = addon?.values?.find(
      (option) => option?._id === value?._id,
    );

    return Boolean(value?.isExhausted || liveValue?.isExhausted);
  });

  const showExhaustedMessage = showExhaustedError || hasExhaustedSelection;

  const showError =
    showValidationError || showExhaustedMessage || needsReplacementSelection;

  const normalizedTitle = String(addon?.title || "")
    .trim()
    .toLowerCase();

  const isSizeAddon =
    normalizedTitle === "size" || normalizedTitle === "cup size";

  const handleKeyboardSelection = (event, addonValue) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();

      onSelect(addon, addonValue);
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{
        overflow: "hidden",

        borderRadius: "18px",

        backgroundColor: COLORS.cardBackground,

        border: showError
          ? `1.5px solid ${COLORS.error}`
          : `1px solid ${COLORS.border}`,

        boxShadow: showError ? "0 0 0 2px rgba(209,67,67,0.05)" : "none",
      }}
    >
      {/* HEADER */}
      <Box
        sx={{
          px: 1.65,

          pt: 1.05,

          pb: isSizeAddon ? 0.25 : 0.6,
        }}
      >
        <Stack direction="row" alignItems="baseline" spacing={0.6}>
          <Typography
            sx={{
              fontSize: isSizeAddon ? 15 : 11,

              fontWeight: isSizeAddon ? 600 : 400,

              color: isSizeAddon ? COLORS.primaryText : "#777777",
            }}
          >
            {isSizeAddon ? "Cup Size" : addon?.title}
          </Typography>

          {!addon?.isOptional && (
            <Typography
              sx={{
                fontSize: 9.5,

                fontWeight: 400,

                color: "#D35A5A",
              }}
            >
              (Any One)
            </Typography>
          )}

          {addon?.isOptional && (
            <Typography
              sx={{
                fontSize: 9.5,

                fontWeight: 400,

                color: "#8D8D8D",
              }}
            >
              (Optional)
            </Typography>
          )}
        </Stack>
      </Box>

      {isSizeAddon ? (
        <SizeAddonOptions
          addon={addon}
          hasExplicitSelection={hasExplicitSelection}
          isSelected={isSelected}
          onSelect={onSelect}
          handleKeyboardSelection={handleKeyboardSelection}
        />
      ) : (
        <StandardAddonOptions
          addon={addon}
          hasExplicitSelection={hasExplicitSelection}
          isSelected={isSelected}
          onSelect={onSelect}
          handleKeyboardSelection={handleKeyboardSelection}
        />
      )}

      {(showValidationError || needsReplacementSelection) && (
        <Typography
          sx={{
            px: 1.65,

            pb: 1.05,

            fontSize: 10.5,

            color: COLORS.error,

            fontWeight: 600,
          }}
        >
          {needsReplacementSelection
            ? `Please select another available option for ${
                addon?.title || "this addon"
              }.`
            : `Please select ${addon?.title || "an option"}.`}
        </Typography>
      )}

      {showExhaustedMessage && (
        <Typography
          sx={{
            px: 1.65,

            pb: 1.05,

            fontSize: 10.5,

            color: COLORS.error,

            fontWeight: 600,
          }}
        >
          Please select another value for {addon?.title || "this addon"}.
        </Typography>
      )}
    </Paper>
  );
};

/*
|--------------------------------------------------------------------------
| SIZE OPTIONS
|--------------------------------------------------------------------------
*/

const SizeAddonOptions = ({
  addon,
  hasExplicitSelection,
  isSelected,
  onSelect,
  handleKeyboardSelection,
}) => {
  const values = addon?.values || [];

  return (
    <Box
      sx={{
        display: "grid",

        gridTemplateColumns: `repeat(${Math.max(
          1,
          values.length,
        )}, minmax(0, 1fr))`,

        alignItems: "end",

        px: 1.4,

        pt: 0.2,

        pb: 1.35,

        gap: 0.4,
      }}
    >
      {values.map((addonValue, valueIndex) => {
        const selected = isSelected(addon?._id, addonValue?._id);

        const isExhausted = Boolean(addonValue?.isExhausted);

        const isDisabled = Boolean(addonValue?.isDisabled);

        const showSelected =
          selected || (!hasExplicitSelection && addonValue?.isDefault);

        const isUnavailable = isDisabled || isExhausted;

        const cupImage = showSelected
          ? addonValue?.selectedImage || addonValue?.nonSelectedImage
          : addonValue?.nonSelectedImage || addonValue?.selectedImage;

        const mainLabel = addonValue?.label || addonValue?.value || "Size";

        const displayLabel = addonValue?.subLabel || mainLabel;

        const additionalPrice = Number(addonValue?.price || 0);

        return (
          <Box
            key={addonValue?._id}
            role="radio"
            aria-checked={showSelected}
            aria-disabled={isUnavailable && !selected}
            tabIndex={0}
            onClick={() => onSelect(addon, addonValue)}
            onKeyDown={(event) => handleKeyboardSelection(event, addonValue)}
            sx={{
              minWidth: 0,

              cursor: isUnavailable && !selected ? "not-allowed" : "pointer",

              display: "flex",

              flexDirection: "column",

              alignItems: "center",

              justifyContent: "flex-end",

              py: 0.35,

              opacity: isUnavailable ? 0.45 : 1,

              outline: "none",

              "&:focus-visible": {
                borderRadius: "10px",

                outline: "2px solid rgba(8,169,238,0.22)",
              },
            }}
          >
            <Box
              sx={{
                width: "100%",

                height: 73,

                display: "flex",

                alignItems: "flex-end",

                justifyContent: "center",
              }}
            >
              {cupImage ? (
                <Box
                  component="img"
                  src={cupImage}
                  alt={String(mainLabel)}
                  sx={{
                    width: "auto",

                    maxWidth: 58,

                    height: getReferenceCupHeight(valueIndex, values.length),

                    objectFit: "contain",

                    objectPosition: "center bottom",

                    display: "block",
                  }}
                />
              ) : (
                <CupShape
                  index={valueIndex}
                  total={values.length}
                  selected={showSelected}
                  label={mainLabel}
                />
              )}
            </Box>

            <Typography
              sx={{
                mt: 0.7,

                fontSize: 15,

                lineHeight: 1.05,

                fontWeight: 400,

                color: COLORS.primaryText,

                textAlign: "center",
              }}
            >
              {displayLabel}
            </Typography>

            {addonValue?.subLabel &&
              addonValue?.label &&
              addonValue?.subLabel !== addonValue?.label && (
                <Typography
                  sx={{
                    mt: 0.3,

                    fontSize: 9.5,

                    color: "#8A8A8A",

                    textAlign: "center",
                  }}
                >
                  {addonValue.label}
                </Typography>
              )}

            {additionalPrice > 0 && (
              <Typography
                sx={{
                  mt: 0.25,

                  fontSize: 9.5,

                  color: COLORS.mutedText,
                }}
              >
                +₹
                {additionalPrice.toFixed(2)}
              </Typography>
            )}

            {isExhausted && (
              <Typography
                sx={{
                  mt: 0.2,

                  fontSize: 9,

                  color: COLORS.error,

                  textAlign: "center",
                }}
              >
                Out of stock
              </Typography>
            )}

            {isDisabled && !isExhausted && (
              <Typography
                sx={{
                  mt: 0.2,

                  fontSize: 9,

                  color: COLORS.error,

                  textAlign: "center",
                }}
              >
                Unavailable
              </Typography>
            )}
          </Box>
        );
      })}
    </Box>
  );
};

/*
|--------------------------------------------------------------------------
| CUP FALLBACK
|--------------------------------------------------------------------------
*/

const getReferenceCupHeight = (index, total = 3) => {
  if (total <= 1) {
    return 52;
  }

  const minHeight = 42;

  const maxHeight = 63;

  return minHeight + ((maxHeight - minHeight) * index) / (total - 1);
};

const CupShape = ({ index, total, selected, label }) => {
  const height = getReferenceCupHeight(index, total);

  const width = height * 0.62;

  const firstCharacter = String(label || "S")
    .trim()
    .charAt(0)
    .toUpperCase();

  return (
    <Box
      sx={{
        width: width,

        height: height,

        position: "relative",

        display: "flex",

        alignItems: "center",

        justifyContent: "center",

        color: selected ? COLORS.white : COLORS.primaryText,

        backgroundColor: selected ? COLORS.primary : "transparent",

        border: `1.3px solid ${selected ? COLORS.primary : COLORS.primaryText}`,

        borderRadius: "2px 2px 4px 4px",

        clipPath: "polygon(7% 0, 93% 0, 82% 100%, 18% 100%)",
      }}
    >
      <Typography
        sx={{
          fontSize: 12,

          lineHeight: 1,

          fontWeight: 500,

          color: "inherit",
        }}
      >
        {firstCharacter}
      </Typography>
    </Box>
  );
};

/*
|--------------------------------------------------------------------------
| STANDARD ADDON OPTIONS
|--------------------------------------------------------------------------
*/

const StandardAddonOptions = ({
  addon,
  hasExplicitSelection,
  isSelected,
  onSelect,
  handleKeyboardSelection,
}) => {
  return (
    <Box
      sx={{
        px: 1.4,

        pb: 1.15,
      }}
    >
      {(addon?.values || []).map((addonValue) => {
        const selected = isSelected(addon?._id, addonValue?._id);

        const isExhausted = Boolean(addonValue?.isExhausted);

        const isDisabled = Boolean(addonValue?.isDisabled);

        const showSelected =
          selected || (!hasExplicitSelection && addonValue?.isDefault);

        const isUnavailable = isDisabled || isExhausted;

        const price = Number(addonValue?.price || 0);

        return (
          <Box
            key={addonValue?._id}
            role={addon?.isMultipleSelect ? "checkbox" : "radio"}
            aria-checked={showSelected}
            aria-disabled={isUnavailable && !selected}
            tabIndex={0}
            onClick={() => onSelect(addon, addonValue)}
            onKeyDown={(event) => handleKeyboardSelection(event, addonValue)}
            sx={{
              minHeight: 46,

              display: "flex",

              alignItems: "center",

              cursor: isUnavailable && !selected ? "not-allowed" : "pointer",

              opacity: isUnavailable ? 0.45 : 1,

              borderRadius: "10px",

              outline: "none",

              "&:focus-visible": {
                outline: "2px solid rgba(8,169,238,0.22)",
              },
            }}
          >
            {/* OPTION NAME */}
            <Box
              sx={{
                flex: 1,

                minWidth: 0,

                pr: 1,
              }}
            >
              <Typography
                sx={{
                  fontSize: 14.5,

                  lineHeight: 1.15,

                  fontWeight: 500,

                  color: COLORS.primaryText,

                  textDecoration: isUnavailable ? "line-through" : "none",
                }}
              >
                {addonValue?.label || addonValue?.value}
              </Typography>

              {addonValue?.subLabel &&
                addonValue?.subLabel !== addonValue?.label && (
                  <Typography
                    sx={{
                      mt: 0.15,

                      fontSize: 9.5,

                      color: COLORS.mutedText,
                    }}
                  >
                    {addonValue.subLabel}
                  </Typography>
                )}

              {isExhausted && (
                <Typography
                  sx={{
                    mt: 0.15,

                    fontSize: 9,

                    color: COLORS.error,
                  }}
                >
                  Out of stock
                </Typography>
              )}

              {isDisabled && !isExhausted && (
                <Typography
                  sx={{
                    mt: 0.15,

                    fontSize: 9,

                    color: COLORS.error,
                  }}
                >
                  Unavailable
                </Typography>
              )}
            </Box>

            {/* PRICE */}
            <Typography
              sx={{
                minWidth: 50,

                mr: 1,

                textAlign: "right",

                fontSize: 13.5,

                fontWeight: 400,

                color: "#606060",
              }}
            >
              ₹{price}
            </Typography>

            {/* RADIO / CHECK */}
            <OptionSelector
              selected={showSelected}
              multiple={addon?.isMultipleSelect}
            />
          </Box>
        );
      })}
    </Box>
  );
};

/*
|--------------------------------------------------------------------------
| CUSTOM OPTION SELECTOR
|--------------------------------------------------------------------------
*/

const OptionSelector = ({ selected, multiple = false }) => {
  if (multiple) {
    return (
      <Box
        sx={{
          width: 28,

          height: 28,

          flexShrink: 0,

          borderRadius: "48px",

          backgroundColor: COLORS.radioBackground,

          display: "grid",

          placeItems: "center",
        }}
      >
        <Box
          sx={{
            width: 13,

            height: 13,

            borderRadius: "44px",

            backgroundColor: selected ? COLORS.primary : "transparent",

            border: selected ? "none" : "1.5px solid #BCD4DF",
          }}
        />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: 28,

        height: 28,

        flexShrink: 0,

        borderRadius: "50%",

        backgroundColor: COLORS.radioBackground,

        display: "grid",

        placeItems: "center",
      }}
    >
      {selected && (
        <Box
          sx={{
            width: 13,

            height: 13,

            borderRadius: "50%",

            backgroundColor: COLORS.primary,
          }}
        />
      )}
    </Box>
  );
};

/*
|--------------------------------------------------------------------------
| QUANTITY
|--------------------------------------------------------------------------
*/

const QuantitySelector = ({ quantity, onDecrease, onIncrease }) => {
  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={0.1}
      sx={{
        height: 54,

        px: 0.35,

        flexShrink: 0,

        border: `1px solid ${COLORS.border}`,

        borderRadius: "27px",

        backgroundColor: COLORS.white,
      }}
    >
      <IconButton
        size="small"
        onClick={onDecrease}
        disabled={quantity <= 1}
        sx={{
          width: 38,

          height: 38,

          color: quantity <= 1 ? COLORS.mutedText : COLORS.primaryText,
        }}
      >
        <RemoveIcon fontSize="small" />
      </IconButton>

      <Typography
        sx={{
          minWidth: 24,

          textAlign: "center",

          fontSize: 14,

          fontWeight: 700,

          color: COLORS.primaryText,
        }}
      >
        {quantity}
      </Typography>

      <IconButton
        size="small"
        onClick={onIncrease}
        sx={{
          width: 38,

          height: 38,

          color: COLORS.primary,
        }}
      >
        <AddIcon fontSize="small" />
      </IconButton>
    </Stack>
  );
};

export default ProductDrawer;
